import {
    type AgentMessage,
    type UserThreadMapping,
} from '@diagon-alley/common';
import useSearchParams from '@hooks/useSearchParams';
import { useCallback } from 'react';

interface SetActiveThreadPayload {
    thread: UserThreadMapping;
    messages?: AgentMessage[];
}

interface UseAiChatThreadPersistenceParams {
    projectUuid?: string | undefined;
    onSetActiveThread: (payload: SetActiveThreadPayload) => void;
    fetchThreadAndMessages: (
        threadId: string,
    ) => Promise<SetActiveThreadPayload>;
    createThread: () => Promise<UserThreadMapping>;
    cookieTtlSeconds?: number; // Defaults to 12 hours
}

interface UseAiChatThreadPersistenceResult {
    restoreThread: () => Promise<void>;
    handleThreadCreated: (
        thread: UserThreadMapping,
        messages?: AgentMessage[],
    ) => void;
    clearCurrentAiThreadCookie: () => void;
}

const THREAD_COOKIE_NAME = 'ai_thread_id';
const DEFAULT_COOKIE_TTL_SECONDS = 60 * 60 * 12;
const AI_CHAT_THREAD_ID_PARAM = 'chatThreadId';

/**
 * Get the current user's organizationId from localStorage to scope
 * AI thread cookies per merchant. Returns undefined if unavailable.
 */
const getOrganizationId = (): string | undefined => {
    try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            return user.organizationId || undefined;
        }
    } catch {
        // Ignore parse errors
    }
    return undefined;
};

/**
 * Build the cookie name scoped to the current merchant.
 * Format: ai_thread_id_{orgId} or ai_thread_id_{projectUuid}_{orgId}
 */
const buildCookieName = (projectUuid?: string): string => {
    const orgId = getOrganizationId();
    const parts = [THREAD_COOKIE_NAME];
    if (projectUuid) parts.push(projectUuid);
    if (orgId) parts.push(orgId);
    return parts.join('_');
};

export const setAiThreadCookie = (
    projectUuid: string | undefined,
    threadId: string,
    maxAgeSeconds: number = DEFAULT_COOKIE_TTL_SECONDS,
) => {
    const name = buildCookieName(projectUuid);
    document.cookie = `${name}=${encodeURIComponent(
        threadId,
    )}; max-age=${maxAgeSeconds}; path=/`;
};

/**
 * Clears all AI thread cookies. Call this on logout to prevent
 * chat threads from persisting across different merchant accounts.
 */
export const clearAllAiThreadCookies = () => {
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
        const name = cookie.split('=')[0];
        if (name.startsWith(THREAD_COOKIE_NAME)) {
            document.cookie = `${name}=; max-age=0; path=/`;
        }
    }
};

const useAiChatThreadPersistence = (
    params: UseAiChatThreadPersistenceParams,
): UseAiChatThreadPersistenceResult => {
    const {
        projectUuid,
        onSetActiveThread,
        fetchThreadAndMessages,
        cookieTtlSeconds = DEFAULT_COOKIE_TTL_SECONDS,
    } = params;
    const aiChatThreadId = useSearchParams(AI_CHAT_THREAD_ID_PARAM);

    const getThreadCookieName = useCallback((projectId?: string): string => {
        return buildCookieName(projectId);
    }, []);

    const getCookie = useCallback((name: string): string | null => {
        const cookies = document.cookie.split('; ');
        const found = cookies.find((cookie) => cookie.startsWith(`${name}=`));
        if (!found) return null;
        try {
            return decodeURIComponent(found.split('=')[1]);
        } catch {
            return found.split('=')[1] ?? null;
        }
    }, []);

    const setCookie = useCallback(
        (name: string, value: string, maxAgeSeconds: number) => {
            document.cookie = `${name}=${encodeURIComponent(
                value,
            )}; max-age=${maxAgeSeconds}; path=/`;
        },
        [],
    );

    const handleThreadCreated = useCallback(
        (thread: UserThreadMapping, messages?: AgentMessage[]) => {
            const cookieName = getThreadCookieName(projectUuid);

            // Always persist chat for now (can add demo mode check later)
            setCookie(cookieName, thread.threadId, cookieTtlSeconds);
            onSetActiveThread({ thread, messages: messages || [] });
        },
        [
            cookieTtlSeconds,
            getThreadCookieName,
            onSetActiveThread,
            projectUuid,
            setCookie,
        ],
    );

    const clearCurrentAiThreadCookie = useCallback(() => {
        const cookieName = getThreadCookieName(projectUuid);
        document.cookie = `${cookieName}=; max-age=0; path=/`;
    }, [getThreadCookieName, projectUuid]);

    const restoreThread = useCallback(async () => {
        const cookieName = getThreadCookieName(projectUuid);

        // Use the query param if it exists, otherwise use the cookie
        const existingThreadId = aiChatThreadId ?? getCookie(cookieName);

        // If the thread id exists, fetch the thread and messages
        if (existingThreadId) {
            try {
                const payload = await fetchThreadAndMessages(existingThreadId);
                onSetActiveThread(payload);
                return;
            } catch (error) {
                console.error(
                    'Failed to restore AI thread from cookie:',
                    error,
                );
            } finally {
                if (aiChatThreadId) {
                    const currentUrl = new URL(window.location.href);
                    currentUrl.searchParams.delete(AI_CHAT_THREAD_ID_PARAM);
                    // Remove query parameter without triggering navigation
                    window.history.replaceState(
                        null,
                        '',
                        currentUrl.pathname + currentUrl.search,
                    );
                }
            }
        }
    }, [
        aiChatThreadId,
        fetchThreadAndMessages,
        getCookie,
        getThreadCookieName,
        onSetActiveThread,
        projectUuid,
    ]);

    return {
        restoreThread,
        handleThreadCreated,
        clearCurrentAiThreadCookie,
    };
};

export default useAiChatThreadPersistence;
